// app/(tabs)/market/gerer/[shopId].tsx
//
// Écran de gestion d'UNE boutique précise (userpro), atteint depuis
// reglages.tsx → "Mes boutiques" → tap sur une boutique. Contient :
//   - Section Apparence : couleurs réelles via ColorPickerField,
//     enregistrées dans market_shops.ui_config via useMyShops.updateUiConfig
//   - Sections Informations / Contacts / Localisation / SEO / Statut :
//     TOUS les champs modifiables de market_shops désormais couverts
//     (patch V11) via useUpdateShop.updateShop — plus seulement
//     cover_image_url comme avant. Chaque section sauvegarde son propre
//     sous-ensemble de champs au blur/à la confirmation, pas de bouton
//     "Enregistrer" global pour rester cohérent avec le pattern déjà en
//     place (ColorPickerField sauvegarde à chaque changement).
//   - Section Produits : liste + bouton "Créer un produit" qui navigue
//     vers l'écran dédié app/(tabs)/market/creer-produit.tsx. Chaque
//     ligne de produit est désormais cliquable et navigue vers
//     market/gerer-produit/[productId].tsx pour l'édition complète du
//     produit (écran à créer séparément — la navigation est prête).
//   - Section Zone de danger (patch V11) : suppression définitive de la
//     boutique, protégée par une confirmation en 3 niveaux : rappel
//     explicite de la cascade sur les produits, obligation de retaper le
//     nom exact de la boutique, bouton Confirmer/Annuler final.
//
// Toute modification de couleur reconstruit l'objet ui_config COMPLET
// avant de l'envoyer au backend (jamais un patch partiel) — la
// contrainte CHECK SQL market_validate_shop_ui_config l'exige.
//
// Bouton retour standard (flèche) en haut à gauche du header pour
// quitter l'écran et revenir à reglages.tsx. Après suppression réussie,
// même redirection vers reglages.tsx.
//
// PATCH V13 : ajout d'une section "Lien public" (juste après la photo de
// couverture, avant "Informations générales") — logique PUREMENT
// FRONTEND, aucun appel backend. Affiche l'URL publique de la boutique
// (https://www.harmoniaworld.world/boutiques/{slug}), construite à
// partir de shop.slug déjà chargé. Un tap sur le texte de l'URL navigue
// DANS l'app vers l'écran public correspondant (app/boutiques/[slug].tsx
// — distinct et indépendant de cet écran de gestion, voir ce fichier
// pour le système public sans session). Un bouton "Copier" à côté copie
// l'URL complète via expo-clipboard, avec un feedback visuel "Copié !"
// pendant 2s (linkCopied + timeout nettoyé au démontage).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.gererShop / market.common). Les
// labels COLOR_FIELDS et STATUS_OPTIONS sont désormais construits à
// l'intérieur du composant (via t()) au lieu d'être des constantes
// statiques hors-composant, pour réagir aux changements de langue.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Switch,
  Modal,
  Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import * as Clipboard from 'expo-clipboard';
import { useTranslation } from 'react-i18next';

import { useMyShops, type MyShop } from '@/hooks/market/useMyShops';
import { useMyShopProducts } from '@/hooks/market/useMyShopProducts';
import { useUpdateShop, type ShopUpdateFields, type ShopStatus } from '@/hooks/market/useUpdateShop';
import { getValidToken } from '@/lib/auth';
import { API_BASE_URL } from '@/lib/api';
import ColorPickerField from '@/components/market/ColorPickerField';
import ImagePickerField from '@/components/market/ImagePickerField';
import type { ShopColors } from '@/lib/market/uiConfigDefaults';
import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';

const NATIVE = Platform.OS !== 'web';

const BRAND = '#6610B1';
const BRAND_SOFT = '#F3E8FF';
const BRAND_TINT = '#F9F5FF';
const BORDER = '#E5D9FF';
const INK = '#222';
const MUTED = '#8A8496';
const DANGER = '#DC2626';
const DANGER_SOFT = '#FEF2F2';
const DANGER_BORDER = '#FCA5A5';

// Domaine public — même domaine que le site web public (voir
// app/boutiques/[slug].tsx). Utilisé uniquement pour construire le lien
// à afficher/copier ici ; ne remplace pas API_BASE_URL (backend Render).
const PUBLIC_SITE_URL = 'https://www.harmoniaworld.world';

function haptic(style: Haptics.ImpactFeedbackStyle = Haptics.ImpactFeedbackStyle.Light) {
  if (NATIVE) Haptics.impactAsync(style);
}

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

// ─── Champ texte réutilisable, sauvegarde au blur ────────────────────────

function ShopField({
  label, value, onChangeText, onBlur, placeholder, keyboardType, multiline, hint,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  onBlur: () => void;
  placeholder?: string;
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad' | 'url';
  multiline?: boolean;
  hint?: string;
}) {
  return (
    <View style={styles.fieldWrap}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.input, multiline && styles.inputMultiline]}
        placeholder={placeholder}
        placeholderTextColor="#B0A8C4"
        value={value}
        onChangeText={onChangeText}
        onBlur={onBlur}
        keyboardType={keyboardType === 'phone-pad' && Platform.OS === 'web' ? 'default' : keyboardType}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        textAlignVertical={multiline ? 'top' : 'center'}
      />
      {!!hint && <Text style={styles.fieldHint}>{hint}</Text>}
    </View>
  );
}

export default function ManageShopScreen() {
  const { shopId } = useLocalSearchParams<{ shopId: string }>();
  const router = useRouter();
  const { t } = useTranslation();

  // ── Listes traduites, construites à chaque render (réagissent au
  // changement de langue) plutôt que des constantes statiques. ──
  const COLOR_FIELDS: { key: keyof ShopColors; label: string }[] = [
    { key: 'primary',    label: t('market.gererShop.colorFields.primary') },
    { key: 'secondary',  label: t('market.gererShop.colorFields.secondary') },
    { key: 'accent',     label: t('market.gererShop.colorFields.accent') },
    { key: 'background', label: t('market.gererShop.colorFields.background') },
    { key: 'card',       label: t('market.gererShop.colorFields.card') },
    { key: 'button',     label: t('market.gererShop.colorFields.button') },
    { key: 'border',     label: t('market.gererShop.colorFields.border') },
    { key: 'text',       label: t('market.gererShop.colorFields.text') },
    { key: 'link',       label: t('market.gererShop.colorFields.link') },
  ];

  const STATUS_OPTIONS: { value: ShopStatus; label: string }[] = [
    { value: 'draft',     label: t('market.common.status.draft') },
    { value: 'published', label: t('market.common.status.published') },
    { value: 'disabled',  label: t('market.common.status.disabled') },
    { value: 'archived',  label: t('market.common.status.archived') },
  ];

  const statusLabel = useCallback((status: string) => {
    return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
  }, [STATUS_OPTIONS]);

  const { shops, loading, error, updateUiConfig, saving: savingUiConfig, saveError: uiConfigSaveError } = useMyShops();
  const [shop, setShop] = useState<MyShop | null>(null);

  const {
    products,
    loading: productsLoading,
    loadingMore: productsLoadingMore,
    error: productsError,
    hasMore: productsHasMore,
    loadMore: loadMoreProducts,
    refresh: refreshProducts,
  } = useMyShopProducts(shopId);

  const {
    updateShop: saveShopFields, saving: savingFields, error: saveFieldsError,
    deleteShop, deleting, deleteError,
  } = useUpdateShop();

  // Produit dont on est en train d'uploader la couverture (pour afficher
  // le bon ImagePickerField en état "uploading" dans la liste).
  const [uploadingProductCoverId, setUploadingProductCoverId] = useState<string | null>(null);

  // ── États locaux des champs texte (édition avant sauvegarde au blur) ──
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [timezone, setTimezone] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // ── Switches (sauvegardés immédiatement au changement) ──
  const [showWhatsappBtn, setShowWhatsappBtn] = useState(true);
  const [showChatBtn, setShowChatBtn] = useState(true);

  // ── Zone de danger : modale de suppression ──
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [confirmNameInput, setConfirmNameInput] = useState('');

  // ── Lien public : feedback "Copié !" temporaire (PATCH V13) ──
  const [linkCopied, setLinkCopied] = useState(false);
  const linkCopiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    };
  }, []);

  // ── AutoScroll + double tap (PATCH V14) ──
  // Même système que DetailProduit.tsx / boutiques/[slug].tsx : pas de
  // header interne animé ici — le topHeader (bouton retour + titre) est
  // en dehors du ScrollView et reste toujours visible. Seule la tab bar
  // globale est cachée/montrée au scroll ou au double tap.
  const { setTabBarHidden } = useTabBarVisibility();
  const tabBarHiddenRef = useRef(false);
  const [lastTap, setLastTap] = useState<number | null>(null);

  const showTabBar = useCallback(() => {
    if (tabBarHiddenRef.current) {
      tabBarHiddenRef.current = false;
      setTabBarHidden(false);
    }
  }, [setTabBarHidden]);

  const hideTabBar = useCallback(() => {
    if (!tabBarHiddenRef.current) {
      tabBarHiddenRef.current = true;
      setTabBarHidden(true);
    }
  }, [setTabBarHidden]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showTabBar,
    onHide: hideTabBar,
  });

  const toggleTabBar = useCallback(() => {
    haptic();
    if (tabBarHiddenRef.current) showTabBar();
    else hideTabBar();
  }, [showTabBar, hideTabBar]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleTabBar();
    else setLastTap(now);
  }, [lastTap, toggleTabBar]);


  useEffect(() => {
    if (shops.length > 0 && shopId) {
      const found = shops.find(s => s.id === shopId);
      if (found) {
        setShop(found);
        // Initialise les champs locaux à partir de la boutique chargée —
        // uniquement au premier chargement pour ne pas écraser une saisie
        // en cours si `shops` se rafraîchit derrière (updateUiConfig fait
        // une mise à jour optimiste de la liste).
        setTagline((prev) => prev || found.tagline || '');
        setDescription((prev) => prev || (found as any).description || '');
        setWhatsappPhone((prev) => prev || (found as any).whatsapp_business_phone || '');
        setContactEmail((prev) => prev || (found as any).contact_email || '');
        setContactPhone((prev) => prev || (found as any).contact_phone || '');
        setCountry((prev) => prev || (found as any).country || '');
        setCity((prev) => prev || (found as any).city || '');
        setAddress((prev) => prev || (found as any).address || '');
        setCurrencyCode((prev) => prev || (found as any).currency_code || 'XOF');
        setLanguageCode((prev) => prev || (found as any).language_code || 'fr');
        setTimezone((prev) => prev || (found as any).timezone || '');
        setSeoTitle((prev) => prev || (found as any).seo_title || '');
        setSeoDescription((prev) => prev || (found as any).seo_description || '');
        setShowWhatsappBtn((found as any).show_whatsapp_business_button ?? true);
        setShowChatBtn((found as any).show_platform_chat_button ?? true);
      }
    }
  }, [shops, shopId]);

  const handleBack = useCallback(() => {
    haptic();
    router.replace('/(tabs)/market/reglages');
  }, [router]);

  const handleColorChange = useCallback((colorKey: keyof ShopColors, nextHex: string) => {
    if (!shop) return;

    // Reconstruction de l'objet ui_config COMPLET — jamais un patch
    // partiel — en ne changeant que la clé colorKey dans colors.
    const nextConfig = {
      ...shop.ui_config,
      colors: {
        ...shop.ui_config.colors,
        [colorKey]: nextHex,
      },
    };

    setShop({ ...shop, ui_config: nextConfig });
    updateUiConfig(shop.id, nextConfig);
  }, [shop, updateUiConfig]);

  // Sauvegarde générique d'un sous-ensemble de champs — utilisée au blur
  // des champs texte et au changement des switches.
  const persistFields = useCallback((fields: ShopUpdateFields) => {
    if (!shop) return;
    saveShopFields(shop.id, fields);
  }, [shop, saveShopFields]);

  const handleGoToCreateProduct = useCallback(() => {
    if (!shop) return;
    haptic();
    router.push(`/(tabs)/market/creer-produit?shopId=${shop.id}`);
  }, [shop, router]);

  // Navigue vers l'écran d'édition complète du produit — chaque ligne de
  // la liste des produits est cliquable.
  const handleGoToProduct = useCallback((productId: string) => {
    haptic();
    router.push(`/(tabs)/market/gerer-produit/${productId}`);
  }, [router]);

  const handleShopCoverUploaded = useCallback((publicUrl: string) => {
    if (!shop) return;
    setShop({ ...shop, cover_image_url: publicUrl } as MyShop);
    saveShopFields(shop.id, { cover_image_url: publicUrl });
  }, [shop, saveShopFields]);

  const handleProductCoverUploaded = useCallback(async (productId: string, publicUrl: string) => {
    setUploadingProductCoverId(null);
    try {
      const token = await getValidToken();
      if (!token) return;
      await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'update-product',
          access_token: token,
          product_id:   productId,
          cover_image_url: publicUrl,
        }),
      });
      refreshProducts();
    } catch (err) {
      console.error('[handleProductCoverUploaded]', err);
    }
  }, [refreshProducts]);

  const handleStatusChange = useCallback((nextStatus: ShopStatus) => {
    if (!shop || shop.status === nextStatus) return;
    haptic();
    setShop({ ...shop, status: nextStatus } as MyShop);
    persistFields({ status: nextStatus });
  }, [shop, persistFields]);

  // ── Lien public (PATCH V13) ──
  // Construit uniquement à partir de shop.slug, déjà chargé — aucun
  // appel réseau supplémentaire.
  const publicShopUrl = useMemo(() => {
    if (!shop?.slug) return null;
    return `${PUBLIC_SITE_URL}/boutiques/${shop.slug}`;
  }, [shop?.slug]);

  // Tap sur le texte de l'URL : navigue DANS l'app vers l'écran public
  // (app/boutiques/[slug].tsx), pas d'ouverture navigateur — décision
  // explicite validée pour cet écran (plus rapide, pas de sortie d'app).
  const handleOpenPublicShop = useCallback(() => {
    if (!shop?.slug) return;
    haptic();
    router.push(`/boutiques/${shop.slug}`);
  }, [shop?.slug, router]);

  const handleCopyPublicShopLink = useCallback(async () => {
    if (!publicShopUrl) return;
    haptic();
    await Clipboard.setStringAsync(publicShopUrl);
    if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    setLinkCopied(true);
    linkCopiedTimeout.current = setTimeout(() => setLinkCopied(false), 2000);
  }, [publicShopUrl]);

  // ── Zone de danger : ouverture/fermeture de la modale ──
  const openDeleteModal = useCallback(() => {
    haptic(Haptics.ImpactFeedbackStyle.Medium);
    setConfirmNameInput('');
    setDeleteModalVisible(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleting) return;
    setDeleteModalVisible(false);
    setConfirmNameInput('');
  }, [deleting]);

  const isConfirmNameValid = useMemo(() => {
    if (!shop) return false;
    return confirmNameInput.trim().toLowerCase() === shop.name.trim().toLowerCase();
  }, [confirmNameInput, shop]);

  const handleConfirmDelete = useCallback(async () => {
    if (!shop || !isConfirmNameValid || deleting) return;
    haptic(Haptics.ImpactFeedbackStyle.Heavy);

    const success = await deleteShop(shop.id);
    if (success) {
      setDeleteModalVisible(false);
      router.replace('/(tabs)/market/reglages');
    }
  }, [shop, isConfirmNameValid, deleting, deleteShop, router]);

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="small" color={BRAND} />
        <Text style={styles.centerText}>{t('market.common.loading')}</Text>
      </View>
    );
  }

  if (error || !shop) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle-outline" size={48} color="#C4B5FD" />
        <Text style={styles.centerText}>
          {error === 'session_expired' ? t('market.common.sessionExpiredReconnect') : t('market.gererShop.notFound')}
        </Text>
        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()} activeOpacity={0.8}>
          <Text style={styles.backBtnText}>{t('market.common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
    {/* ── Header avec bouton retour ── */}
    <View style={styles.topHeader}>
      <TouchableOpacity style={styles.topBackBtn} activeOpacity={0.7} onPress={handleBack}>
        <Ionicons name="arrow-back" size={24} color="#333" />
      </TouchableOpacity>
      <Text style={styles.topHeaderTitle} numberOfLines={1}>{shop.name}</Text>
      <View style={styles.topHeaderSpacer} />
    </View>

    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      onTouchEnd={handleDoubleTap}
    >
      <View style={styles.headerBlock}>
        <Ionicons name="storefront" size={28} color={BRAND} />
        <Text style={styles.shopName}>{shop.name}</Text>
        <Text style={styles.shopStatus}>{statusLabel(shop.status)}</Text>
      </View>

      {/* ── Image de couverture de la boutique ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.coverPhoto')}</Text>
        <ImagePickerField
          context="shop-cover"
          ids={{ shop_id: shop.id }}
          currentImageUrl={shop.cover_image_url}
          onUploaded={handleShopCoverUploaded}
        />
      </View>

      {/* ── Lien public (PATCH V13) ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.publicLink')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <Text style={styles.fieldHint}>
              {t('market.gererShop.publicLinkHint')}
            </Text>
            {publicShopUrl ? (
              <>
                <TouchableOpacity
                  style={styles.publicLinkRow}
                  activeOpacity={0.7}
                  onPress={handleOpenPublicShop}
                >
                  <Ionicons name="link-outline" size={16} color={BRAND} />
                  <Text style={styles.publicLinkText} numberOfLines={1}>{publicShopUrl}</Text>
                  <Ionicons name="chevron-forward" size={16} color={MUTED} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.copyLinkBtn}
                  activeOpacity={0.8}
                  onPress={handleCopyPublicShopLink}
                >
                  <Ionicons
                    name={linkCopied ? 'checkmark' : 'copy-outline'}
                    size={16}
                    color={linkCopied ? '#16A34A' : BRAND}
                  />
                  <Text style={[styles.copyLinkBtnText, linkCopied && styles.copyLinkBtnTextSuccess]}>
                    {linkCopied ? t('market.common.copied') : t('market.common.copy')}
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <Text style={styles.fieldHint}>{t('market.gererShop.publicLinkPending')}</Text>
            )}
          </View>
        </View>
      </View>

      {(savingUiConfig || savingFields) && (
        <View style={styles.savingBanner}>
          <ActivityIndicator size="small" color={BRAND} />
          <Text style={styles.savingBannerText}>{t('market.common.saving')}</Text>
        </View>
      )}

      {(!!uiConfigSaveError || !!saveFieldsError) && (
        <View style={styles.errorBanner}>
          <Ionicons name="alert-circle-outline" size={16} color={DANGER} />
          <Text style={styles.errorBannerText}>
            {(uiConfigSaveError || saveFieldsError) === 'session_expired' ? t('market.common.sessionExpired') : t('market.gererShop.saveError')}
          </Text>
        </View>
      )}

      {/* ── Informations générales ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.generalInfo')}</Text>
        <View style={styles.sectionCard}>
          <ShopField
            label={t('market.gererShop.fields.taglineLabel')}
            value={tagline}
            onChangeText={setTagline}
            onBlur={() => persistFields({ tagline })}
            placeholder={t('market.gererShop.fields.taglinePlaceholder')}
            hint={t('market.gererShop.fields.taglineHint')}
          />
          <ShopField
            label={t('market.gererShop.fields.descriptionLabel')}
            value={description}
            onChangeText={setDescription}
            onBlur={() => persistFields({ description })}
            placeholder={t('market.gererShop.fields.descriptionPlaceholder')}
            multiline
          />
        </View>
      </View>

      {/* ── Contacts ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.contacts')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <ShopField
              label={t('market.gererShop.fields.whatsappLabel')}
              value={whatsappPhone}
              onChangeText={setWhatsappPhone}
              onBlur={() => persistFields({ whatsapp_business_phone: whatsappPhone })}
              placeholder={t('market.gererShop.fields.whatsappPlaceholder')}
              keyboardType="phone-pad"
              hint={t('market.gererShop.fields.whatsappHint')}
            />
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>{t('market.gererShop.fields.showWhatsappBtn')}</Text>
              <Switch
                value={showWhatsappBtn}
                onValueChange={(v) => {
                  setShowWhatsappBtn(v);
                  persistFields({ show_whatsapp_business_button: v });
                }}
                trackColor={{ false: '#E5E1EC', true: BRAND }}
                thumbColor="#fff"
              />
            </View>
            <View style={[styles.toggleRow, { marginTop: 14 }]}>
              <Text style={styles.toggleLabel}>{t('market.gererShop.fields.showChatBtn')}</Text>
              <Switch
                value={showChatBtn}
                onValueChange={(v) => {
                  setShowChatBtn(v);
                  persistFields({ show_platform_chat_button: v });
                }}
                trackColor={{ false: '#E5E1EC', true: BRAND }}
                thumbColor="#fff"
              />
            </View>
            <View style={styles.divider} />
            <ShopField
              label={t('market.gererShop.fields.contactEmailLabel')}
              value={contactEmail}
              onChangeText={setContactEmail}
              onBlur={() => persistFields({ contact_email: contactEmail })}
              placeholder={t('market.gererShop.fields.contactEmailPlaceholder')}
              keyboardType="email-address"
            />
            <ShopField
              label={t('market.gererShop.fields.contactPhoneLabel')}
              value={contactPhone}
              onChangeText={setContactPhone}
              onBlur={() => persistFields({ contact_phone: contactPhone })}
              placeholder={t('market.gererShop.fields.contactPhonePlaceholder')}
              keyboardType="phone-pad"
            />
          </View>
        </View>
      </View>

      {/* ── Localisation ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.location')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <ShopField
              label={t('market.gererShop.fields.countryLabel')}
              value={country}
              onChangeText={setCountry}
              onBlur={() => persistFields({ country })}
              placeholder={t('market.gererShop.fields.countryPlaceholder')}
            />
            <ShopField
              label={t('market.gererShop.fields.cityLabel')}
              value={city}
              onChangeText={setCity}
              onBlur={() => persistFields({ city })}
              placeholder={t('market.gererShop.fields.cityPlaceholder')}
            />
            <ShopField
              label={t('market.gererShop.fields.addressLabel')}
              value={address}
              onChangeText={setAddress}
              onBlur={() => persistFields({ address })}
              placeholder={t('market.gererShop.fields.addressPlaceholder')}
            />
            <ShopField
              label={t('market.gererShop.fields.currencyLabel')}
              value={currencyCode}
              onChangeText={(v) => setCurrencyCode(v.toUpperCase())}
              onBlur={() => persistFields({ currency_code: currencyCode })}
              placeholder={t('market.gererShop.fields.currencyPlaceholder')}
              hint={t('market.gererShop.fields.currencyHint')}
            />
            <ShopField
              label={t('market.gererShop.fields.languageLabel')}
              value={languageCode}
              onChangeText={setLanguageCode}
              onBlur={() => persistFields({ language_code: languageCode })}
              placeholder="fr"
            />
            <ShopField
              label={t('market.gererShop.fields.timezoneLabel')}
              value={timezone}
              onChangeText={setTimezone}
              onBlur={() => persistFields({ timezone })}
              placeholder={t('market.gererShop.fields.timezonePlaceholder')}
            />
          </View>
        </View>
      </View>

      {/* ── SEO ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.seo')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <ShopField
              label={t('market.gererShop.fields.seoTitleLabel')}
              value={seoTitle}
              onChangeText={setSeoTitle}
              onBlur={() => persistFields({ seo_title: seoTitle })}
              placeholder={t('market.gererShop.fields.seoTitlePlaceholder')}
            />
            <ShopField
              label={t('market.gererShop.fields.seoDescriptionLabel')}
              value={seoDescription}
              onChangeText={setSeoDescription}
              onBlur={() => persistFields({ seo_description: seoDescription })}
              placeholder={t('market.gererShop.fields.seoDescriptionPlaceholder')}
              multiline
            />
          </View>
        </View>
      </View>

      {/* ── Statut ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.status')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.sectionCardInner}>
            <View style={styles.chipRow}>
              {STATUS_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.chip, shop.status === option.value && styles.chipActive]}
                  activeOpacity={0.7}
                  onPress={() => handleStatusChange(option.value)}
                >
                  <Text style={[styles.chipText, shop.status === option.value && styles.chipTextActive]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={styles.fieldHint}>
              {t('market.gererShop.statusHint')}
            </Text>
          </View>
        </View>
      </View>

      {/* ── Apparence : couleurs (réelles) ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.colors')}</Text>
        <View style={styles.sectionCard}>
          {COLOR_FIELDS.map((field) => (
            <ColorPickerField
              key={field.key}
              label={field.label}
              value={shop.ui_config.colors[field.key]}
              onChange={(hex) => handleColorChange(field.key, hex)}
            />
          ))}
        </View>
      </View>

      {/* ── Typographie / Mise en page / En-tête (à venir) ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.typography')}</Text>
        <View style={styles.sectionCard}>
          <View style={styles.centerBox}>
            <Text style={styles.centerBoxText}>{t('market.gererShop.typographyPending')}</Text>
          </View>
        </View>
      </View>

      {/* ── Produits de cette boutique ── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('market.gererShop.sections.products')}</Text>
        <View style={styles.sectionCard}>
          {productsLoading && (
            <View style={styles.centerBox}>
              <ActivityIndicator size="small" color={BRAND} />
            </View>
          )}

          {!productsLoading && productsError && (
            <View style={styles.centerBox}>
              <Text style={styles.centerBoxText}>{t('market.gererShop.cannotLoadProducts')}</Text>
            </View>
          )}

          {!productsLoading && !productsError && products.length === 0 && (
            <View style={styles.centerBox}>
              <Text style={styles.centerBoxText}>{t('market.gererShop.noProducts')}</Text>
            </View>
          )}

          {!productsLoading && products.map((product) => (
            <TouchableOpacity
              key={product.id}
              style={styles.productRow}
              activeOpacity={0.7}
              onPress={() => handleGoToProduct(product.id)}
            >
              <View style={styles.productRowImageWrap}>
                <ImagePickerField
                  context="product-cover"
                  ids={{ shop_id: shop.id, product_id: product.id }}
                  currentImageUrl={product.cover_image_url}
                  onUploaded={(url) => handleProductCoverUploaded(product.id, url)}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.productRowName} numberOfLines={1}>{product.name}</Text>
                <Text style={styles.productRowMeta}>
                  {formatPrice(product.price, product.currency_code)} · {statusLabel(product.status)}
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={MUTED} />
            </TouchableOpacity>
          ))}

          {productsLoadingMore && (
            <View style={styles.centerBox}>
              <ActivityIndicator size="small" color={BRAND} />
            </View>
          )}

          {!productsLoading && productsHasMore && !productsLoadingMore && (
            <TouchableOpacity style={styles.loadMoreBtn} activeOpacity={0.7} onPress={loadMoreProducts}>
              <Text style={styles.loadMoreBtnText}>{t('market.common.seeMore')}</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity style={styles.createShopBtn} activeOpacity={0.8} onPress={handleGoToCreateProduct}>
            <Ionicons name="add-circle" size={20} color={BRAND} />
            <Text style={styles.createShopBtnText}>{t('market.gererShop.createProductButton')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* ── Zone de danger ── */}
      <View style={styles.section}>
        <Text style={[styles.sectionTitle, styles.dangerSectionTitle]}>{t('market.gererShop.sections.dangerZone')}</Text>
        <View style={styles.dangerCard}>
          <Text style={styles.dangerTitle}>{t('market.gererShop.dangerTitle')}</Text>
          <Text style={styles.dangerText}>
            {t('market.gererShop.dangerText')}
          </Text>
          <TouchableOpacity style={styles.dangerBtn} activeOpacity={0.8} onPress={openDeleteModal}>
            <Ionicons name="trash-outline" size={18} color="#fff" />
            <Text style={styles.dangerBtnText}>{t('market.gererShop.dangerButton')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={{ height: 40 }} />
    </ScrollView>

    {/* ── Modale de suppression (confirmation en 3 niveaux) ── */}
    <Modal
      visible={deleteModalVisible}
      transparent
      animationType="fade"
      onRequestClose={closeDeleteModal}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <View style={styles.modalIconWrap}>
            <Ionicons name="warning" size={26} color={DANGER} />
          </View>
          <Text style={styles.modalTitle}>{t('market.gererShop.deleteModal.title', { name: shop.name })}</Text>
          <Text style={styles.modalText}>
            {t('market.gererShop.deleteModal.text', { count: products.length + (productsHasMore ? '+' : '') })}
          </Text>

          <Text style={styles.modalFieldLabel}>
            {t('market.gererShop.deleteModal.confirmLabel')}
          </Text>
          <Text style={styles.modalShopNameHint}>{shop.name}</Text>
          <TextInput
            style={styles.modalInput}
            placeholder={shop.name}
            placeholderTextColor="#C4B5FD"
            value={confirmNameInput}
            onChangeText={setConfirmNameInput}
            autoCapitalize="none"
            autoCorrect={false}
            editable={!deleting}
          />

          {!!deleteError && (
            <Text style={styles.modalErrorText}>
              {deleteError === 'session_expired' ? t('market.common.sessionExpiredReconnect') : t('market.gererShop.saveError')}
            </Text>
          )}

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              activeOpacity={0.7}
              onPress={closeDeleteModal}
              disabled={deleting}
            >
              <Text style={styles.modalCancelBtnText}>{t('market.common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.modalConfirmBtn,
                (!isConfirmNameValid || deleting) && styles.modalConfirmBtnDisabled,
              ]}
              activeOpacity={0.8}
              onPress={handleConfirmDelete}
              disabled={!isConfirmNameValid || deleting}
            >
              {deleting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.modalConfirmBtnText}>{t('market.gererShop.deleteModal.confirmButton')}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F0F2F5',
  },
  content: {
    padding: 16,
  },
  topHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  topBackBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topHeaderTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: '#222',
    textAlign: 'center',
    marginHorizontal: 4,
  },
  topHeaderSpacer: {
    width: 40,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 12,
    backgroundColor: '#fff',
  },
  centerText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  backBtn: {
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: BRAND_SOFT,
    borderRadius: 10,
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: BRAND,
  },
  headerBlock: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  shopName: {
    fontSize: 20,
    fontWeight: '800',
    color: '#222',
    marginTop: 8,
  },
  shopStatus: {
    fontSize: 13,
    color: '#999',
    marginTop: 4,
  },
  savingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: BRAND_SOFT,
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  savingBannerText: {
    fontSize: 12.5,
    color: BRAND,
    fontWeight: '600',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF2F2',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  errorBannerText: {
    fontSize: 12.5,
    color: DANGER,
    fontWeight: '600',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
    marginLeft: 4,
  },
  dangerSectionTitle: {
    color: DANGER,
  },
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  sectionCardInner: {
    paddingHorizontal: 14,
    paddingVertical: 16,
  },
  centerBox: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  centerBoxText: {
    fontSize: 13,
    color: '#999',
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  fieldWrap: {
    marginBottom: 14,
  },
  fieldLabel: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#666',
    marginBottom: 7,
  },
  fieldHint: {
    fontSize: 11,
    color: MUTED,
    marginTop: 5,
  },
  input: {
    backgroundColor: BRAND_TINT,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 14 : 10,
    fontSize: 15,
    color: '#333',
  },
  inputMultiline: {
    minHeight: 90,
    paddingTop: 12,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleLabel: {
    fontSize: 13.5,
    fontWeight: '600',
    color: INK,
    flex: 1,
    marginRight: 12,
  },
  divider: {
    height: 1,
    backgroundColor: '#F0EDF5',
    marginVertical: 16,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: BRAND_TINT,
    borderWidth: 1,
    borderColor: BORDER,
  },
  chipActive: {
    backgroundColor: BRAND,
    borderColor: BRAND,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: BRAND,
  },
  chipTextActive: {
    color: '#fff',
  },
  // ── Lien public (PATCH V13) ──
  publicLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: BRAND_TINT,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginTop: 10,
  },
  publicLinkText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: BRAND,
  },
  copyLinkBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: BRAND_SOFT,
    borderRadius: 12,
    paddingVertical: 11,
    marginTop: 10,
  },
  copyLinkBtnText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: BRAND,
  },
  copyLinkBtnTextSuccess: {
    color: '#16A34A',
  },
  productRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  productRowImageWrap: {
    width: 48,
  },
  productRowName: {
    fontSize: 14.5,
    fontWeight: '700',
    color: '#222',
  },
  productRowMeta: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  loadMoreBtn: {
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  loadMoreBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: BRAND,
  },
  createShopBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    backgroundColor: BRAND_TINT,
  },
  createShopBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND,
  },
  // ── Zone de danger ──
  dangerCard: {
    backgroundColor: DANGER_SOFT,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: DANGER_BORDER,
    padding: 16,
  },
  dangerTitle: {
    fontSize: 14.5,
    fontWeight: '800',
    color: DANGER,
    marginBottom: 6,
  },
  dangerText: {
    fontSize: 12.5,
    color: '#991B1B',
    lineHeight: 18,
    marginBottom: 14,
  },
  dangerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: DANGER,
    borderRadius: 12,
    paddingVertical: 13,
  },
  dangerBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
  },
  // ── Modale de suppression ──
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17, 17, 20, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalCard: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 22,
  },
  modalIconWrap: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: DANGER_SOFT,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: INK,
    marginBottom: 8,
  },
  modalText: {
    fontSize: 13,
    color: MUTED,
    lineHeight: 19,
    marginBottom: 16,
  },
  modalFieldLabel: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#666',
    marginBottom: 4,
  },
  modalShopNameHint: {
    fontSize: 13,
    fontWeight: '700',
    color: DANGER,
    marginBottom: 8,
  },
  modalInput: {
    backgroundColor: DANGER_SOFT,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DANGER_BORDER,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 14 : 10,
    fontSize: 15,
    color: '#333',
    marginBottom: 8,
  },
  modalErrorText: {
    fontSize: 12,
    color: DANGER,
    fontWeight: '600',
    marginBottom: 8,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: 12,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCancelBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#4B5563',
  },
  modalConfirmBtn: {
    flex: 1.4,
    paddingVertical: 13,
    borderRadius: 12,
    backgroundColor: DANGER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalConfirmBtnDisabled: {
    backgroundColor: '#FCA5A5',
  },
  modalConfirmBtnText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#fff',
  },
});
